import React, { useState } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	Paper,
	Radio,
	RadioGroup,
	Typography,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import LoginTwoToneIcon from "@mui/icons-material/LoginTwoTone";
import LogoutTwoToneIcon from "@mui/icons-material/LogoutTwoTone";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IVisitorTicket } from "../type/newVisitorType";
import type { ITicketScanPayload, ITicketListItem } from "../type/ticketScanType";

interface Props {
	open: boolean;
	handleClose: () => void;
	ticket: IVisitorTicket | ITicketListItem | null;
	visitorId?: number;
}

const TicketWiseScanModal: React.FC<Props> = ({
	open,
	handleClose,
	ticket,
	visitorId,
}) => {
	const theme = useTheme();
	const [scanType, setScanType] = useState<"IN" | "OUT">("IN");

	const { mutate: scanTicket, isPending } = useApiMutation<
		ITicketScanPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/v1/admin/visitor/ticket/scan",
		invalidateQueries: [
			"VisitorDetails",
			"AllVisitorTickets",
			"TicketScans",
		],
	});

	const handleScan = () => {
		if (!ticket?.id) return;

		const payload: ITicketScanPayload = {
			ticket_id: ticket.id,
			scan_type: scanType,
		};

		scanTicket(payload, {
			onSuccess: () => {
				handleClose();
			},
		});
	};

	const onDialogClose = () => {
		if (isPending) return;
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onDialogClose}
			maxWidth="xs"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<QrCodeScannerTwoToneIcon color="primary" />
					<Typography variant="h4" fontWeight={600}>
						Scan Ticket
					</Typography>
				</Box>
				<IconButton onClick={onDialogClose} size="small" disabled={isPending}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2, pb: 1.5 }}>
				{ticket && (
					<Box sx={{ mb: 1.5, textAlign: "center" }}>
						<Chip
							label={ticket.ticket_no || `#${ticket.id}`}
							color="primary"
							variant="filled"
							size="small"
							sx={{ fontWeight: 700, fontSize: "0.875rem", mb: 0.5 }}
						/>
						<Typography variant="caption" display="block" color="text.secondary">
							Total Amount:{" "}
							<strong>{ticket.total_amount || "0.00"}</strong> | Qty:{" "}
							<strong>{ticket.quantity || 1}</strong>
						</Typography>
					</Box>
				)}

				<Typography
					variant="caption"
					fontWeight={700}
					color="text.secondary"
					textTransform="uppercase"
					letterSpacing={0.5}
					sx={{ display: "block", mb: 0.75 }}
				>
					Select Scan Type
				</Typography>

				<FormControl component="fieldset" fullWidth>
					<RadioGroup
						value={scanType}
						onChange={(e) => setScanType(e.target.value as "IN" | "OUT")}
						sx={{ display: "flex", flexDirection: "column", gap: 1 }}
					>
						<Paper
							variant="outlined"
							sx={{
								px: 1.5,
								py: 0.75,
								borderRadius: 1.5,
								borderColor:
									scanType === "IN" ? "primary.main" : "divider",
								bgcolor:
									scanType === "IN"
										? alpha(theme.palette.primary.main, 0.08)
										: "transparent",
								cursor: "pointer",
								transition: "all 0.2s",
							}}
							onClick={() => setScanType("IN")}
						>
							<FormControlLabel
								value="IN"
								sx={{ width: "100%", m: 0 }}
								control={<Radio size="small" color="primary" sx={{ p: 0.5, mr: 0.5 }} />}
								label={
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
										<LoginTwoToneIcon fontSize="small" color="primary" />
										<Box>
											<Typography
												variant="body2"
												fontWeight={600}
												color={
													scanType === "IN"
														? "primary.main"
														: "text.primary"
												}
											>
												IN (Entrance Entry)
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: "block", fontSize: "0.75rem", lineHeight: 1.2 }}
											>
												Scan visitor entry into fair venue
											</Typography>
										</Box>
									</Box>
								}
							/>
						</Paper>

						<Paper
							variant="outlined"
							sx={{
								px: 1.5,
								py: 0.75,
								borderRadius: 1.5,
								borderColor:
									scanType === "OUT" ? "primary.main" : "divider",
								bgcolor:
									scanType === "OUT"
										? alpha(theme.palette.primary.main, 0.08)
										: "transparent",
								cursor: "pointer",
								transition: "all 0.2s",
							}}
							onClick={() => setScanType("OUT")}
						>
							<FormControlLabel
								value="OUT"
								sx={{ width: "100%", m: 0 }}
								control={<Radio size="small" color="primary" sx={{ p: 0.5, mr: 0.5 }} />}
								label={
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
										<LogoutTwoToneIcon fontSize="small" color="primary" />
										<Box>
											<Typography
												variant="body2"
												fontWeight={600}
												color={
													scanType === "OUT"
														? "primary.main"
														: "text.primary"
												}
											>
												OUT (Exit Gate)
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: "block", fontSize: "0.75rem", lineHeight: 1.2 }}
											>
												Scan visitor exit from fair venue
											</Typography>
										</Box>
									</Box>
								}
							/>
						</Paper>
					</RadioGroup>
				</FormControl>
			</DialogContent>

			<Divider />

			<DialogActions sx={{ px: 2.5, py: 1.5 }}>
				<Button
					onClick={onDialogClose}
					size="small"
					variant="outlined"
					disabled={isPending}
					sx={{
						borderColor: "grey.400",
						color: "grey.700",
						"&:hover": { backgroundColor: "grey.100" },
					}}
				>
					Cancel
				</Button>
				<LoadingButton
					onClick={handleScan}
					size="small"
					variant="contained"
					color="primary"
					loading={isPending}
				>
					<span>Confirm Scan</span>
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default TicketWiseScanModal;
